import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useShop } from "./ShopContext";
import { detectLang, localize, toItPath } from "../lib/i18n";

const LocaleContext = createContext(null);

// Default alternates (it/en) ricavati dal solo pathname. Le detail page dinamiche
// li sovrascrivono con gli slug precisi tramite registerAltUrls().
function defaultAlts(pathname) {
  const itPath = toItPath(pathname);
  return { it: itPath, en: localize(itPath, "en") };
}

export const LocaleProvider = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const pathname = location.pathname;
  const lang = detectLang(pathname);
  const { setLang } = useShop();

  const [altUrls, setAltUrls] = useState(() => defaultAlts(pathname));

  // Redirect degli URL legacy ?lang=xx ai nuovi path localizzati (301-like, replace).
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const q = params.get("lang");
    if (q !== "it" && q !== "en") return;
    params.delete("lang");
    const rest = params.toString();
    const itPath = toItPath(pathname);
    const target = (q === "en" ? localize(itPath, "en") : itPath) + (rest ? `?${rest}` : "");
    if (target !== pathname + location.search) {
      navigate(target, { replace: true });
    }
  }, [pathname, location.search, navigate]);

  // Tiene ShopContext (t(), content[lang]) allineato alla lingua dell'URL.
  useEffect(() => {
    setLang(lang);
  }, [lang, setLang]);

  // Reset degli alternates al default a ogni cambio pagina; le detail page
  // ri-registrano i propri subito dopo.
  useEffect(() => {
    setAltUrls(defaultAlts(pathname));
  }, [pathname]);

  // Le detail page chiamano questo con gli URL IT/EN reali (slug + slug_en).
  const registerAltUrls = useCallback((it, en) => {
    setAltUrls({ it, en });
  }, []);

  // Helper: localizza un path IT-canonico nella lingua corrente.
  const lp = useCallback((itPath, enSlug) => localize(itPath, lang, enSlug), [lang]);

  const value = useMemo(
    () => ({ lang, altUrls, registerAltUrls, lp }),
    [lang, altUrls, registerAltUrls, lp],
  );

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
};

export const useLocale = () => {
  const ctx = useContext(LocaleContext);
  if (!ctx) throw new Error("useLocale must be used within LocaleProvider");
  return ctx;
};
